import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { STEPS } from '@/shared/walkthrough-steps';
import { usePersona, useWalkthrough } from '@/app/providers';
import { useIsDemoMode } from '@/versions/v1/chrome/Walkthrough';

// useWalkthroughSync — equivalent of the source mock's `onHashChange` +
// persona auto-flip logic, ported to React Router. On every route change:
//   1. resolve the current path+search to a STEPS index
//   2. if the path matches a step, auto-flip the persona to STEPS[idx].persona
//   3. update the walkthrough's currentStep to keep the indicator in sync
//
// The hook ignores updates while in `freeExplore` mode — the indicator should
// stay frozen so the user can wander off-spine without losing their place.
//
// 2026-05-17 audit D1 gate: the hook is a no-op when the walkthrough is
// gated off (no `?demo=true`). Default users get no persona auto-flip and
// no freeExplore writes — the walkthrough is invisible end-to-end.
export function useWalkthroughSync() {
  const isDemo = useIsDemoMode();
  const location = useLocation();
  const { setPersona } = usePersona();
  const { freeExplore, setCurrentStep, setFreeExplore } = useWalkthrough();
  const lastPath = useRef<string>('');

  useEffect(() => {
    if (!isDemo) return;
    const here = location.pathname + (location.search ?? '');
    if (here === lastPath.current) return;
    lastPath.current = here;

    if (freeExplore) return;

    // Try exact match first, then base-path match (no query).
    let idx = STEPS.findIndex((s) => s.hash === here);
    if (idx === -1) {
      const base = here.split('?')[0];
      idx = STEPS.findIndex((s) => s.hash.split('?')[0] === base);
    }
    if (idx === -1) {
      // User wandered off-spine — pause the walkthrough into Free Explore
      // rather than fighting back with a redirect.
      setFreeExplore(true);
      return;
    }

    setCurrentStep(idx);
    setPersona(STEPS[idx].persona);
  }, [isDemo, location.pathname, location.search, freeExplore, setCurrentStep, setFreeExplore, setPersona]);
}
